import { useState, useEffect } from 'react';
import { getSeasonsOverview, extendSeason } from '../lib/api';
import type { SeasonOverviewUser } from '../lib/api';

function buildDateWindow(pastDays: number, futureDays: number): string[] {
  const dates: string[] = [];
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  for (let i = -pastDays; i <= futureDays; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

function formatShortDate(iso: string): string {
  const [, month, day] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
}

function formatDayAbbr(iso: string): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[new Date(iso + 'T00:00:00').getDay()];
}

type CellStatus = 'workout' | 'rest' | 'none';

function getCellStatus(
  user: SeasonOverviewUser,
  date: string
): CellStatus {
  const type = user.dates[date];
  if (!type) return 'none';
  return type;
}

function cellClass(status: CellStatus): string {
  switch (status) {
    case 'workout':
      return 'bg-green-500 rounded';
    case 'rest':
      return 'bg-green-200 rounded';
    case 'none':
      return 'bg-white border border-gray-200 rounded';
  }
}

export function SeasonDashboard() {
  const [users, setUsers] = useState<SeasonOverviewUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [extendingUsers, setExtendingUsers] = useState<Set<string>>(new Set());
  const [extendedUsers, setExtendedUsers] = useState<Set<string>>(new Set());
  const [extendErrors, setExtendErrors] = useState<Record<string, string>>({});
  const [isExtendingAll, setIsExtendingAll] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const dates = buildDateWindow(14, 28); // today-14 to today+28

  const load = () => {
    setIsLoading(true);
    setError(null);
    getSeasonsOverview()
      .then(setUsers)
      .catch((e: Error) => setError(e.message))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleExtendUser = async (userId: string) => {
    setExtendingUsers(prev => new Set(prev).add(userId));
    setExtendErrors(prev => { const n = { ...prev }; delete n[userId]; return n; });
    try {
      await extendSeason(userId);
      setExtendedUsers(prev => new Set(prev).add(userId));
      load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to extend';
      setExtendErrors(prev => ({ ...prev, [userId]: msg }));
    } finally {
      setExtendingUsers(prev => { const n = new Set(prev); n.delete(userId); return n; });
    }
  };

  const handleExtendAll = async () => {
    setIsExtendingAll(true);
    const withSeasons = users.filter(u => Object.keys(u.dates).length > 0);
    for (const user of withSeasons) {
      await handleExtendUser(user.id);
    }
    setIsExtendingAll(false);
  };

  const usersWithSeasons = users.filter(u => Object.keys(u.dates).length > 0);

  return (
    <div className="max-w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">All Users Overview</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing {dates.length} days: {formatShortDate(dates[0])} – {formatShortDate(dates[dates.length - 1])}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExtendAll}
            disabled={isExtendingAll || isLoading || usersWithSeasons.length === 0}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isExtendingAll ? 'Extending All…' : 'Extend All'}
          </button>
          <button
            onClick={load}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded" />
          <span>Workout day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-200 border border-gray-200 rounded" />
          <span>Rest day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border border-gray-200 rounded" />
          <span>No plan</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-orange-400 rounded" />
          <span>Today</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <p className="mt-3 text-gray-500 text-sm">Loading season data…</p>
        </div>
      )}

      {!isLoading && users.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-xs">
          <table className="border-collapse text-xs" style={{ minWidth: 'max-content' }}>
            <thead>
              <tr className="bg-gray-50">
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-semibold text-gray-700 border-b border-r border-gray-200 min-w-[220px]">
                  User
                </th>
                {dates.map((date) => (
                  <th
                    key={date}
                    className={`px-1 py-2 text-center font-medium border-b border-gray-200 min-w-[36px] ${
                      date === today
                        ? 'bg-orange-50 text-orange-700 border-x-2 border-x-orange-400'
                        : 'text-gray-500'
                    }`}
                  >
                    <div>{formatDayAbbr(date)}</div>
                    <div className="text-gray-400 font-normal" style={{ fontSize: '0.65rem' }}>
                      {formatShortDate(date)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, rowIdx) => {
                const hasSeason = Object.keys(user.dates).length > 0;
                const isExtending = extendingUsers.has(user.id);
                const wasExtended = extendedUsers.has(user.id) && !isExtending;
                const extendError = extendErrors[user.id];
                return (
                  <tr
                    key={user.id}
                    className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                  >
                    <td className={`sticky left-0 z-10 px-4 py-2 border-b border-r border-gray-200 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-800 whitespace-nowrap">
                          {user.first_name} {user.last_name}
                        </span>
                        {hasSeason && (
                          <div className="flex items-center gap-1 shrink-0">
                            {extendError && (
                              <span
                                className="text-red-500 text-xs cursor-help"
                                title={extendError}
                              >
                                ✕
                              </span>
                            )}
                            {wasExtended && (
                              <span className="text-green-600 text-xs">✓</span>
                            )}
                            <button
                              onClick={() => handleExtendUser(user.id)}
                              disabled={isExtending || isExtendingAll}
                              className="px-2 py-0.5 text-xs bg-green-100 text-green-700 border border-green-300 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Extend this user's plan by one week"
                            >
                              {isExtending ? '…' : 'Extend'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    {dates.map((date) => {
                      const status = getCellStatus(user, date);
                      const isToday = date === today;
                      return (
                        <td
                          key={date}
                          className={`px-1 py-2 border-b border-gray-100 text-center ${
                            isToday ? 'border-x-2 border-x-orange-400' : ''
                          }`}
                          title={`${user.first_name} ${user.last_name} — ${date}: ${
                            status === 'none' ? 'no plan' : status
                          }`}
                        >
                          <div className={`w-5 h-5 mx-auto ${cellClass(status)}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !error && users.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No users found.
        </div>
      )}

      {!isLoading && users.length > 0 && (
        <div className="mt-4 text-xs text-gray-400">
          {users.length} user{users.length !== 1 ? 's' : ''} shown
          {' · '}
          {usersWithSeasons.length} with active plans
        </div>
      )}
    </div>
  );
}
